import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../api'
import { CompanyType } from '../../common/constants'
import { ReactComponent as ExportIcon } from '../../assets/images/icons/ic-export.svg'
import Card from '../../components/cards/Card'
import { apiErrorText } from '../../utils/apiError'
import Button from '../../components/Button'
import MetricStatBlock from '../../components/analytics/MetricStatBlock'
import Dialog from '../../components/dialogs/Dialog'
import HeaderFooterLayout from '../../components/layouts/HeaderFooterLayout'
import Pagination from '../../components/Pagination'
import Select from '../../components/Select'
import { ReactComponent as FilterIcon } from '../../assets/images/icons/ic-filter.svg'
import StudentProgress from '../../components/StudentProgress'
import Table from '../../components/Table'
import TextInput from '../../components/TextInput'
import typo from '../../typography.module.css'
import styles from './CompanyDashboard.module.css'

const COMPANY_DASHBOARD_CONFIG = {
  [CompanyType.LeadGenerationProvider]: {
    title: 'Dashboard Lead Generation',
    subtitle: 'Spazio operativo per partner orientati alla lead generation e integrazioni API.',
  },
  [CompanyType.B2BTeaching]: {
    title: 'Dashboard B2B Teaching',
    subtitle: 'Monitora utenti, corsi e avanzamento della formazione aziendale.',
  },
  [CompanyType.TrainingBuyer]: {
    title: 'Dashboard Training Buyer',
    subtitle: 'Spazio operativo per aziende acquirenti di formazione con integrazione dedicata.',
  },
  [CompanyType.Other]: {
    title: 'Dashboard Azienda',
    subtitle: 'Spazio operativo per aziende partner con integrazione attiva.',
  },
}

const getStudentViewedLessonsValue = (student) => {
  if (!student?.modules || !Array.isArray(student.modules)) {
    return 0
  }

  return student.modules.reduce((total, module) => {
    const completedLessons = Array.isArray(module?.lessons)
      ? module.lessons.filter((lesson) => lesson?.mandatory !== false && lesson?.completed).length
      : 0
    return total + completedLessons
  }, 0)
}

const getStudentTotalLessonsValue = (student) => {
  if (!student?.modules || !Array.isArray(student.modules)) {
    return 0
  }

  return student.modules.reduce((total, module) => {
    return total + (Array.isArray(module?.lessons) ? module.lessons.filter((lesson) => lesson?.mandatory !== false).length : 0)
  }, 0)
}

const getStudentProgressValue = (student) => {
  if (!student) {
    return 0
  }

  if (student.edition_type === 'live') {
    const duration = Number(student.edition_duration) || 0
    const progress = Number(student.edition_seconds_progress) || 0
    if (duration <= 0) {
      return 0
    }
    const percentage = Math.round((progress * 100) / duration)
    return Number.isNaN(percentage) ? 0 : Math.max(0, Math.min(100, percentage))
  }

  const totalLessons = getStudentTotalLessonsValue(student)
  const viewedLessons = getStudentViewedLessonsValue(student)
  if (totalLessons <= 0) {
    return 0
  }

  const percentage = Math.round((viewedLessons / totalLessons) * 100)
  return Number.isNaN(percentage) ? 0 : percentage
}

const CompanyDashboard = ({ user }) => {
  const navigate = useNavigate()
  const [dashboardData, setDashboardData] = useState(null)
  const [loadingDashboard, setLoadingDashboard] = useState(false)
  const [pageError, setPageError] = useState('')
  const [progresses, setProgresses] = useState([])
  const [loadingProgresses, setLoadingProgresses] = useState(false)
  const [progressesCount, setProgressesCount] = useState(0)
  const [progressesPage, setProgressesPage] = useState(0)
  const [progressesPer] = useState(25)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [courseOptions, setCourseOptions] = useState([])
  const [filteredCourseOptions, setFilteredCourseOptions] = useState([])
  const [studentsSearchQuery, setStudentsSearchQuery] = useState('')
  const [studentProgressDetail, setStudentProgressDetail] = useState(null)
  const [exportingProgressesCsv, setExportingProgressesCsv] = useState(false)

  const config = useMemo(() => {
    return COMPANY_DASHBOARD_CONFIG[user?.company?.type] || COMPANY_DASHBOARD_CONFIG[CompanyType.Other]
  }, [user])

  const welcomeTitle = useMemo(() => {
    const firstName = String(user?.name || '').trim()
    return firstName ? `Benvenuto, ${firstName}` : 'Benvenuto'
  }, [user?.name])

  const isB2BTeaching = user?.company?.type === CompanyType.B2BTeaching

  const loadDashboard = useCallback(async () => {
    if (!isB2BTeaching) {
      return
    }

    setLoadingDashboard(true)
    try {
      const response = await api.get('/company/dashboard')
      setDashboardData(response)

      const mappedCourses = (response?.courses || []).map((companyCourse) => ({
        id: companyCourse?.course?.id ?? companyCourse?.course_id,
        value: companyCourse?.course?.id ?? companyCourse?.course_id,
        name: companyCourse?.course?.name || `Corso ${companyCourse?.course_id}`,
      })).sort((a, b) => a.name.localeCompare(b.name, 'it'))

      setCourseOptions(mappedCourses)
      setFilteredCourseOptions(mappedCourses)
    } catch (e) {
      console.error(e)
      setPageError(apiErrorText(e))
    }
    setLoadingDashboard(false)
  }, [isB2BTeaching])

  const fetchProgresses = useCallback(async ({ page = progressesPage, q = studentsSearchQuery, courseId = selectedCourse?.value ?? null } = {}) => {
    if (!isB2BTeaching) {
      return []
    }

    setPageError('')
    setLoadingProgresses(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        per: String(progressesPer),
      })

      if (q?.trim()) {
        params.set('q', q.trim())
      }
      if (courseId) {
        params.set('course_id', String(courseId))
      }

      const response = await api.get(`/company/dashboard/progresses?${params.toString()}`, { timeout: 0 })
      const items = response?.progresses || []
      setProgresses(items)
      setProgressesCount(response?.count || 0)
      return items
    } catch (e) {
      console.error(e)
      setPageError(apiErrorText(e))
      setProgresses([])
      setProgressesCount(0)
      return []
    } finally {
      setLoadingProgresses(false)
    }
  }, [isB2BTeaching, progressesPage, progressesPer, selectedCourse, studentsSearchQuery])

  useEffect(() => {
    loadDashboard()
  }, [loadDashboard])

  useEffect(() => {
    if (isB2BTeaching) {
      fetchProgresses({ page: progressesPage })
    }
  }, [fetchProgresses, isB2BTeaching, progressesPage, selectedCourse, studentsSearchQuery])

  const onStudentsSearch = useCallback((value) => {
    setStudentsSearchQuery(String(value || ''))
    setProgressesPage(0)
  }, [])

  const onCourseSearch = useCallback((value) => {
    const query = String(value || '').trim().toLowerCase()
    if (!query) {
      setFilteredCourseOptions(courseOptions)
      return
    }

    setFilteredCourseOptions(
      courseOptions.filter((course) => String(course?.name || '').toLowerCase().includes(query))
    )
  }, [courseOptions])

  const escapeCsvValue = useCallback((value) => {
    const normalized = String(value ?? '')
    if (/[,\n";]/.test(normalized)) {
      return `"${normalized.replace(/"/g, '""')}"`
    }
    return normalized
  }, [])

  const exportProgressesCsv = useCallback(async () => {
    if (!isB2BTeaching || exportingProgressesCsv) {
      return
    }

    setExportingProgressesCsv(true)
    try {
      const per = 100
      let page = 0
      let total = null
      const allStudents = []

      while (total === null || allStudents.length < total) {
        const params = new URLSearchParams({
          page: String(page),
          per: String(per),
        })

        if (studentsSearchQuery?.trim()) {
          params.set('q', studentsSearchQuery.trim())
        }
        if (selectedCourse?.value) {
          params.set('course_id', String(selectedCourse.value))
        }

        const response = await api.get(`/company/dashboard/progresses?${params.toString()}`, { timeout: 0 })
        const items = Array.isArray(response?.progresses) ? response.progresses : []
        const count = Number(response?.count || 0)

        if (total === null) {
          total = count
        }

        allStudents.push(...items)

        if (!items.length) {
          break
        }

        page += 1
      }

      const header = [
        'Corso',
        'Studente ID',
        'Nome',
        'Cognome',
        'Email',
        'Tipo edizione',
        'Modulo',
        'Lezione ID',
        'Lezione',
        'Tipo lezione',
        'Posizione',
        'Inizio lezione',
        'Fine lezione',
        'Vista il',
        'Completata',
        'Durata lezione (s)',
        'Durata video (s)',
        'Live fruita (s)',
        'Recorded fruita (s)',
        'Live %',
        'Recorded %',
        'Registrato il',
        'Ultimo accesso',
      ]

      const rows = allStudents.flatMap((student) => {
        const registeredAt = student?.registered_at ? new Date(student.registered_at).toLocaleString('it-IT') : ''
        const lastAccess = student?.last_access ? new Date(student.last_access).toLocaleString('it-IT') : ''
        const modules = Array.isArray(student?.modules) ? student.modules : []

        return modules.flatMap((module) => {
          const lessons = Array.isArray(module?.lessons) ? module.lessons : []
          return lessons.map((lesson) => {
            const lessonDuration = Number(lesson?.duration || 0)
            const lessonVideoDuration = Number(lesson?.video_duration || 0)
            const liveDuration = Number(lesson?.live_duration || 0)
            const recordedDuration = Number(lesson?.recorded_duration || 0)
            const livePercentage = lessonDuration > 0 ? Math.min(100, Math.round((liveDuration * 100) / lessonDuration)) : 0
            const recordedPercentage = lessonVideoDuration > 0 ? Math.min(100, Math.round((recordedDuration * 100) / lessonVideoDuration)) : 0
            const completed = Boolean(lesson?.completed) || livePercentage >= 80 || recordedPercentage >= 90

            return [
              student?.course_name || '',
              student?.id || '',
              student?.name || '',
              student?.surname || '',
              student?.email || '',
              student?.edition_type || '',
              module?.name || '',
              lesson?.id || '',
              lesson?.name || '',
              lesson?.type || '',
              lesson?.position || '',
              lesson?.starts_at ? new Date(lesson.starts_at).toLocaleString('it-IT') : '',
              (lesson?.ended_at || lesson?.ends_at) ? new Date(lesson.ended_at || lesson.ends_at).toLocaleString('it-IT') : '',
              lesson?.viewed_at ? new Date(lesson.viewed_at).toLocaleString('it-IT') : '',
              completed ? 'true' : 'false',
              lessonDuration,
              lessonVideoDuration,
              liveDuration,
              recordedDuration,
              livePercentage,
              recordedPercentage,
              registeredAt,
              lastAccess,
            ]
          })
        })
      })

      const csvContent = [header, ...rows]
        .map((line) => line.map((cell) => escapeCsvValue(cell)).join(','))
        .join('\n')

      const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'progressi-studenti-azienda.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e) {
      console.error(e)
      setPageError(apiErrorText(e))
    } finally {
      setExportingProgressesCsv(false)
    }
  }, [escapeCsvValue, exportingProgressesCsv, isB2BTeaching, selectedCourse?.value, studentsSearchQuery])

  const courseRows = useMemo(() => {
    return (dashboardData?.courses || []).map((companyCourse) => ({
      ...companyCourse,
      id: companyCourse.id,
      course_name: companyCourse?.course?.name || `Corso ${companyCourse?.course_id}`,
      max_users: companyCourse?.max_users ?? 0,
      registered_users: companyCourse?.registered_users ?? 0,
      available_slots: {
        value: companyCourse?.available_slots ?? 0,
        type: 'number',
        component: <div className={styles.remainingValue}>{companyCourse?.available_slots ?? 0}</div>,
      },
    }))
  }, [dashboardData?.courses])

  const progressRows = useMemo(() => {
    return (progresses || []).map((student) => ({
      ...student,
      id: `${student.id}-${student.course_id ?? 'course'}`,
      course_name: student.course_name || 'N/A',
      lessons: {
        value: getStudentViewedLessonsValue(student),
        type: 'number',
        component: <div style={{ whiteSpace: 'nowrap' }}>{`${getStudentViewedLessonsValue(student)} / ${getStudentTotalLessonsValue(student)}`}</div>,
      },
      progress: {
        value: getStudentProgressValue(student),
        type: 'number',
        component: <div style={{ whiteSpace: 'nowrap' }}>{getStudentProgressValue(student)}%</div>,
      },
    }))
  }, [progresses])

  if (!isB2BTeaching) {
    return (
      <HeaderFooterLayout>
        <div className={styles.container}>
          <div className={styles.section}>
            <div className={styles.sectionInner}>
              <div className={typo.title}>{welcomeTitle}</div>
              <div className={styles.subtitle}>{config.subtitle}</div>

              <div className={styles.cardsGrid}>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>Chiavi API</div>
                  <div className={styles.cardDescription}>
                    Gestisci chiavi, monitora utilizzo e verifica chiamate della tua integrazione.
                  </div>
                  <Button appearance='subtle' onClick={() => navigate('/developers')}>
                    APRI DEVELOPERS
                  </Button>
                </div>

                <div className={styles.card}>
                  <div className={styles.cardTitle}>Documentazione API</div>
                  <div className={styles.cardDescription}>
                    Consulta endpoint, autenticazione e tool di verifica firma per i test.
                  </div>
                  <Button appearance='subtle' onClick={() => navigate('/developers/documentation')}>
                    APRI DOCUMENTAZIONE
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeaderFooterLayout>
    )
  }

  return (
    <HeaderFooterLayout>
      <div className={`${styles.container} ${styles.teaching}`}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <h1 className={styles.pageTitle}>{welcomeTitle}</h1>
            <div className={styles.subtitle}>{config.subtitle}</div>

            {pageError && <div role="alert" className={styles.dialogError}>{pageError}</div>}
            <div className={styles.analyticsRow}>
              <MetricStatBlock
                title='UTENTI AZIENDA'
                actualValue={loadingDashboard ? null : (dashboardData?.users_count || 0)}
                loading={loadingDashboard}
                containerClassName={styles.analyticCard}
                titleClassName={styles.analyticTitle}
                actualClassName={styles.actualAnalytic}
                previousClassName={styles.previousAnalytic}
                skeletonClassName={styles.analyticSkeleton}
              />
              <MetricStatBlock
                title='CORSI ASSOCIATI'
                actualValue={loadingDashboard ? null : (dashboardData?.courses_count || 0)}
                loading={loadingDashboard}
                containerClassName={styles.analyticCard}
                titleClassName={styles.analyticTitle}
                actualClassName={styles.actualAnalytic}
                previousClassName={styles.previousAnalytic}
                skeletonClassName={styles.analyticSkeleton}
              />
              <MetricStatBlock
                title='POSTI DISPONIBILI'
                actualValue={loadingDashboard ? null : (dashboardData?.available_slots || 0)}
                loading={loadingDashboard}
                containerClassName={styles.analyticCard}
                titleClassName={styles.analyticTitle}
                actualClassName={styles.actualAnalytic}
                previousClassName={styles.previousAnalytic}
                skeletonClassName={styles.analyticSkeleton}
              />
            </div>

            <Card appearance="modern" integratedTable title="Corsi aziendali">
                <Table integrated loading={loadingDashboard} loadingN={4}
                  columns={[
                    { key: 'course_name', label: 'CORSO', type: 'string' },
                    { key: 'max_users', label: 'LIMITE', type: 'number' },
                    { key: 'registered_users', label: 'ISCRITTI', type: 'number' },
                    { key: 'available_slots', label: 'POSTI DISPONIBILI', type: 'custom' },
                  ]}
                  elements={courseRows}
                  onRowClick={(course) => {
                    const targetCourseId = course?.course_id ?? course?.course?.id
                    if (!targetCourseId) {
                      return
                    }
                    const targetCourseName = course?.course_name || course?.course?.name || 'Dettaglio corso'
                    navigate(`/dashboard/courses/${targetCourseId}`, {
                      state: {
                        courseName: targetCourseName,
                      },
                    })
                  }}
                  defaultSort={{ key: 'course_name', direction: 'asc' }}
                />
                {!loadingDashboard && !pageError && courseRows.length === 0 && <p className={styles.emptyState}>Non ci sono corsi associati alla tua azienda.</p>}
            </Card>

            <Card appearance="modern" integratedTable title="Avanzamento studenti" actions={(<Button appearance='subtle' disabled={exportingProgressesCsv || loadingProgresses} onClick={exportProgressesCsv}>
                    <ExportIcon aria-hidden="true" style={{ width: 11, height: 11 }} />
                    {exportingProgressesCsv ? 'Esportazione...' : 'Esporta'}
                  </Button>)}>
              <div className={styles.filtersRow}>
                <div className={styles.filterSearch}><TextInput appearance="modern" onKeyUp={onStudentsSearch} type="search" placeholder="Cerca studente" /></div>
                <Button appearance="subtle" additionalClass={styles.filterToggle} aria-expanded={filtersOpen} aria-controls="company-course-filters" onClick={() => setFiltersOpen(open => !open)}>
                  <FilterIcon aria-hidden="true" /> Filtri{selectedCourse ? ' (1)' : ''}
                </Button>
              </div>
              {filtersOpen && <div id="company-course-filters" className={styles.filterPanel}>
                <Select appearance="modern" portal selected={selectedCourse} options={filteredCourseOptions}
                  nullable nullLabel="Tutti i corsi" placeholder="Filtra per corso" onSearch={onCourseSearch} searchPlaceholder="Cerca corso"
                  onSelect={course => { setSelectedCourse(course?.value ? course : null); setProgressesPage(0) }} />
              </div>}

              <Table
                integrated
                columns={[
                  { key: 'course_name', label: 'CORSO', type: 'string' },
                  { key: 'name', label: 'NOME', type: 'string' },
                  { key: 'surname', label: 'COGNOME', type: 'string' },
                  { key: 'email', label: 'EMAIL', type: 'string' },
                  { key: 'lessons', label: 'LEZIONI', type: 'custom' },
                  { key: 'progress', label: 'PROGRESSO', type: 'custom' },
                  { key: 'registered_at', label: 'REGISTRATO IL', type: 'datetime' },
                  { key: 'last_access', label: 'ULTIMO ACCESSO', type: 'datetime' },
                ]}
                loading={loadingProgresses}
                loadingN={6}
                onRowClick={(student) => setStudentProgressDetail(student)}
                defaultSort={{ key: 'surname', direction: 'asc' }}
                elements={progressRows}
              />

              {!loadingProgresses && !pageError && progresses.length === 0 && <p className={styles.emptyState}>Nessuno studente trovato. Prova a modificare la ricerca.</p>}
              <div className={styles.pagination}><Pagination
                page={progressesPage}
                total={progressesCount}
                per={progressesPer}
                onChange={setProgressesPage}
                loading={loadingProgresses}
              /></div>
            </Card>
          </div>
        </div>

        {studentProgressDetail && (
          <Dialog appearance="modern"
            title={`${studentProgressDetail.name} ${studentProgressDetail.surname}`}
            open={!!studentProgressDetail}
            onClose={() => setStudentProgressDetail(null)}
            style={{ width: '94vw', maxWidth: '1120px' }}
          >
            <StudentProgress modern student={studentProgressDetail} canSetLessonCompleted={false} />
          </Dialog>
        )}
      </div>
    </HeaderFooterLayout>
  )
}

export default CompanyDashboard
